import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Chart from 'react-apexcharts';
import { metricsApi, readingsApi, servicesApi, initiativesApi } from '../services/trackerApi';
import { peopleApi } from '../services/api';
import { useToast } from '../hooks/useToast';
import { PageHeader } from '../components/PageHeader';
import { NimbusTable } from '../components/NimbusTable';
import { DateField } from '../components/DateField';
import { formatDate } from '../utils/format';
import { SEVERITY_TONE } from '../constants/tone';

export const MetricDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const { data: metrics = [] } = useQuery({ queryKey: ['metrics'], queryFn: metricsApi.getAll });
  const { data: readings = [] } = useQuery({ queryKey: ['readings', id], queryFn: () => readingsApi.list(id!), enabled: !!id });
  const { data: services = [] } = useQuery({ queryKey: ['services'], queryFn: servicesApi.getAll });
  const { data: people = [] } = useQuery({ queryKey: ['people'], queryFn: peopleApi.getAll });
  const { data: initiatives = [] } = useQuery({ queryKey: ['initiatives'], queryFn: initiativesApi.getAll });

  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [value, setValue] = useState('');
  const [note, setNote] = useState('');
  const [editingMetric, setEditingMetric] = useState(false);
  const [metricForm, setMetricForm] = useState({ name: '', unit: '', direction: 'higher', cadence: 'Monthly', target: '', baseline: '', criticality: 'Medium' });
  const [confirmDeleteMetric, setConfirmDeleteMetric] = useState(false);
  const [editingReading, setEditingReading] = useState<string | null>(null);
  const [readingForm, setReadingForm] = useState({ date: '', value: '', note: '' });
  const [confirmDeleteReading, setConfirmDeleteReading] = useState<string | null>(null);

  const metric = metrics.find((m: any) => m.metricId === id);
  const service = services.find((s: any) => s.serviceId === metric?.serviceId);
  const originInitiative = initiatives.find((i: any) => i.initiativeId === metric?.originInitiativeId);
  const bauOwner = people.find((p: any) => p.personId === metric?.bauOwnerId);

  const addReading = useMutation({
    mutationFn: () => readingsApi.create(id!, { date, value: Number(value), note }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['readings', id] });
      setValue('');
      setNote('');
      toast.success('Reading recorded.');
    },
  });

  const updateMetric = useMutation({
    mutationFn: () =>
      metricsApi.update(id!, {
        name: metricForm.name,
        unit: metricForm.unit,
        direction: metricForm.direction,
        cadence: metricForm.cadence,
        target: metricForm.target === '' ? null : Number(metricForm.target),
        baseline: metricForm.baseline === '' ? null : Number(metricForm.baseline),
        criticality: metricForm.criticality,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['metrics'] });
      setEditingMetric(false);
      toast.success('Metric updated.');
    },
    onError: (e: any) => toast.error(e.message || 'Failed to update metric'),
  });

  const deleteMetric = useMutation({
    mutationFn: () => metricsApi.delete(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['metrics'] });
      toast.success('Metric deleted.');
      navigate(service ? `/tracker/catalog/services/${service.serviceId}` : '/tracker/catalog');
    },
    onError: (e: any) => toast.error(e.message || 'Failed to delete metric'),
  });

  const updateReading = useMutation({
    mutationFn: (readingId: string) => readingsApi.update(id!, readingId, { date: readingForm.date, value: Number(readingForm.value), note: readingForm.note }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['readings', id] });
      setEditingReading(null);
      toast.success('Reading updated.');
    },
    onError: (e: any) => toast.error(e.message || 'Failed to update reading'),
  });

  const deleteReading = useMutation({
    mutationFn: (readingId: string) => readingsApi.delete(id!, readingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['readings', id] });
      setConfirmDeleteReading(null);
      toast.success('Reading removed.');
    },
    onError: (e: any) => toast.error(e.message || 'Failed to remove reading'),
  });

  if (!metric) return <p className="np-sub container-xxl flex-grow-1 container-p-y">Loading…</p>;

  const sorted = [...readings].sort((a: any, b: any) => a.date.localeCompare(b.date));

  const startEditMetric = () => {
    setMetricForm({
      name: metric.name, unit: metric.unit, direction: metric.direction, cadence: metric.cadence,
      target: metric.target != null ? String(metric.target) : '', baseline: metric.baseline != null ? String(metric.baseline) : '',
      criticality: metric.criticality || 'Medium',
    });
    setEditingMetric(true);
  };

  const startEditReading = (r: any) => {
    setReadingForm({ date: r.date, value: String(r.value), note: r.note || '' });
    setEditingReading(r.readingId);
  };

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-line-chart-line"
        title={metric.name}
        breadcrumbs={[
          { label: 'Service Catalog', to: '/tracker/catalog' },
          ...(service ? [{ label: service.name, to: `/tracker/catalog/services/${service.serviceId}` }] : []),
          { label: metric.name },
        ]}
        actions={
          <>
            <button className="np-icon-btn" onClick={startEditMetric} title="Edit metric"><i className="ri-edit-line"></i></button>
            <button className="np-icon-btn np-icon-btn-danger" onClick={() => setConfirmDeleteMetric(true)} title="Delete metric"><i className="ri-delete-bin-line"></i></button>
          </>
        }
      />

      {!editingMetric && (
        <div className="d-flex gap-2 mb-3 flex-wrap">
          <span className={`np-chip np-tone-${metric.origin === 'Project' ? 'primary' : 'neutral'}`}>{metric.origin}</span>
          {metric.trackedAsBau && <span className="np-chip np-tone-success">Tracked as BAU</span>}
          <span className={`np-chip np-tone-${SEVERITY_TONE[(metric.criticality || 'Medium') as keyof typeof SEVERITY_TONE]}`}>{metric.criticality || 'Medium'} criticality</span>
          <span className="np-chip np-tone-neutral">{metric.cadence}</span>
          <span className="np-chip np-tone-neutral">{metric.direction === 'higher' ? 'Higher is better' : 'Lower is better'}</span>
        </div>
      )}
      {confirmDeleteMetric && (
        <div className="np-banner np-tone-danger mb-3 d-flex align-items-center gap-2" style={{ display: 'flex' }}>
          <span className="flex-grow-1">Delete this metric and its whole reading history? This can't be undone.</span>
          <button className="np-btn np-btn-danger np-btn-sm" disabled={deleteMetric.isPending} onClick={() => deleteMetric.mutate()}>Yes, delete</button>
          <button className="np-btn np-btn-ghost np-btn-sm" onClick={() => setConfirmDeleteMetric(false)}>Cancel</button>
        </div>
      )}

      {editingMetric && (
        <div className="np-card">
          <div className="np-card-head"><h2>Edit metric</h2></div>
          <div className="np-card-body">
            <div className="row g-2 mb-2">
              <div className="col-md-6"><input className="np-input" placeholder="Name" value={metricForm.name} onChange={(e) => setMetricForm({ ...metricForm, name: e.target.value })} /></div>
              <div className="col-md-3"><input className="np-input" placeholder="Unit" value={metricForm.unit} onChange={(e) => setMetricForm({ ...metricForm, unit: e.target.value })} /></div>
              <div className="col-md-3">
                <select className="np-select" value={metricForm.direction} onChange={(e) => setMetricForm({ ...metricForm, direction: e.target.value })}>
                  <option value="higher">Higher is better</option>
                  <option value="lower">Lower is better</option>
                </select>
              </div>
            </div>
            <div className="row g-2 mb-2">
              <div className="col-md-4">
                <select className="np-select" value={metricForm.cadence} onChange={(e) => setMetricForm({ ...metricForm, cadence: e.target.value })}>
                  {['Weekly', 'Monthly', 'Quarterly', 'Bi-annually', 'Annually'].map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="col-md-4"><input className="np-input" placeholder="Baseline" value={metricForm.baseline} onChange={(e) => setMetricForm({ ...metricForm, baseline: e.target.value })} /></div>
              <div className="col-md-4"><input className="np-input" placeholder="Target" value={metricForm.target} onChange={(e) => setMetricForm({ ...metricForm, target: e.target.value })} /></div>
            </div>
            <div className="row g-2 mb-2">
              <div className="col-md-4">
                <select className="np-select" value={metricForm.criticality} onChange={(e) => setMetricForm({ ...metricForm, criticality: e.target.value })}>
                  {['Low', 'Medium', 'High', 'Critical'].map((c) => <option key={c} value={c}>{c} criticality</option>)}
                </select>
              </div>
            </div>
            <div className="d-flex gap-2">
              <button className="np-btn np-btn-primary np-btn-sm" disabled={!metricForm.name.trim() || updateMetric.isPending} onClick={() => updateMetric.mutate()}>Save changes</button>
              <button className="np-btn np-btn-ghost np-btn-sm" onClick={() => setEditingMetric(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div className="np-grid np-grid-8-4">
        {/* .np-page > .np-card's automatic vertical rhythm (nimbus-premium.css) only applies to
            direct children of .np-page - cards stacked inside a grid column are grandchildren,
            so they get none of it and end up touching with zero gap. */}
        <div className="d-flex flex-column gap-3">
          <div className="np-card">
            <div className="np-card-body">
              {sorted.length > 0 ? (
                <Chart
                  type="line"
                  height={260}
                  series={[{ name: metric.name, data: sorted.map((r: any) => ({ x: new Date(r.date).getTime(), y: r.value })) }]}
                  options={{
                    chart: { toolbar: { show: false }, animations: { enabled: false } },
                    stroke: { curve: 'smooth', width: 3 },
                    colors: ['var(--bs-primary)'],
                    xaxis: { type: 'datetime', labels: { style: { colors: 'var(--bs-secondary-color)' } } },
                    yaxis: { labels: { style: { colors: 'var(--bs-secondary-color)' } } },
                    grid: { borderColor: 'var(--bs-border-color)' },
                    dataLabels: { enabled: false },
                    // Always a well-formed annotations object, never `undefined` for "no
                    // target" - ApexCharts' initial mount tolerates that fine, but its update
                    // path (hit every time this page re-renders after a mutation, e.g. editing
                    // the metric or logging a reading) doesn't: it reads
                    // config.annotations.images unconditionally and throws when annotations
                    // itself is undefined. An empty yaxis array is the "no annotation" state
                    // instead.
                    annotations: {
                      yaxis: metric.target != null
                        ? [{ y: metric.target, borderColor: 'var(--bs-success)', label: { text: `Target: ${metric.target}`, style: { background: 'var(--bs-success)', color: 'var(--bs-white)', fontSize: '10px' } } }]
                        : [],
                    },
                  }}
                />
              ) : (
                <div className="text-center py-4">
                  <i className="ri-line-chart-line d-block mb-2" style={{ fontSize: '1.75rem', color: 'var(--bs-secondary-color)' }}></i>
                  <p className="np-sub mb-0">No readings yet.</p>
                </div>
              )}
            </div>
          </div>

          <div className="np-card">
            <div className="np-card-head"><h2>Log a reading</h2></div>
            <div className="np-card-body">
              <div className="row g-2 mb-2">
                <div className="col-md-4"><DateField className="np-input" value={date} onChange={setDate} /></div>
                <div className="col-md-3"><input className="np-input" placeholder={`Value (${metric.unit})`} value={value} onChange={(e) => setValue(e.target.value)} /></div>
                <div className="col-md-5"><input className="np-input" placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} /></div>
              </div>
              <button className="np-btn np-btn-primary np-btn-sm" disabled={!value.trim()} onClick={() => addReading.mutate()}>Add reading</button>
            </div>
          </div>
        </div>

        <div className="d-flex flex-column gap-3">
          <div className="np-card">
            <div className="np-card-head"><h2>Details</h2></div>
            <div className="np-card-body">
              <div className="np-meta-row"><span className="np-meta-label">Baseline</span><span className="np-meta-value">{metric.baseline ?? '—'} {metric.unit}</span></div>
              <div className="np-meta-row"><span className="np-meta-label">Target</span><span className="np-meta-value">{metric.target ?? '—'} {metric.unit}</span></div>
              <div className="np-meta-row"><span className="np-meta-label">BAU owner</span><span className="np-meta-value">{bauOwner?.personName || 'Not yet assigned'}</span></div>
            </div>
          </div>

          {originInitiative && (
            <div className="np-card">
              <div className="np-card-head"><h2>Born from a project</h2></div>
              <div className="np-card-body">
                <Link to={`/tracker/initiatives/${originInitiative.initiativeId}`} className="np-row-title d-flex align-items-center gap-2">
                  <i className="ri-flashlight-line"></i>{originInitiative.name}
                </Link>
                <p className="np-sub mt-2 mb-1">Independent variables it changed:</p>
                <div className="d-flex flex-wrap gap-1">
                  {(originInitiative.independentVariables || []).map((v: string, i: number) => (
                    <span key={i} className="np-chip np-tone-neutral">{v}</span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {sorted.length > 0 && (
        <div className="np-card">
          <div className="np-card-head"><h2>Reading history</h2></div>
          <NimbusTable thead={<tr><th>Date</th><th>Value</th><th>Source</th><th>Note</th><th></th></tr>}>
            {[...sorted].reverse().map((r: any) => (
              editingReading === r.readingId ? (
                <tr key={r.readingId}>
                  <td><DateField className="np-input" value={readingForm.date} onChange={(v) => setReadingForm({ ...readingForm, date: v })} /></td>
                  <td><input className="np-input" style={{ width: 90 }} value={readingForm.value} onChange={(e) => setReadingForm({ ...readingForm, value: e.target.value })} /></td>
                  <td><span className={`np-chip np-tone-${r.source === 'report' ? 'info' : 'neutral'}`}>{r.source}</span></td>
                  <td><input className="np-input" value={readingForm.note} onChange={(e) => setReadingForm({ ...readingForm, note: e.target.value })} /></td>
                  <td className="text-end">
                    <button className="np-btn np-btn-primary np-btn-sm me-1" disabled={updateReading.isPending} onClick={() => updateReading.mutate(r.readingId)}>Save</button>
                    <button className="np-btn np-btn-ghost np-btn-sm" onClick={() => setEditingReading(null)}>Cancel</button>
                  </td>
                </tr>
              ) : (
                <tr key={r.readingId}>
                  <td>{formatDate(r.date)}</td>
                  <td>{r.value} {metric.unit}</td>
                  <td><span className={`np-chip np-tone-${r.source === 'report' ? 'info' : 'neutral'}`}>{r.source}</span></td>
                  <td className="np-sub">{r.note}</td>
                  <td className="text-end">
                    {confirmDeleteReading === r.readingId ? (
                      <>
                        <span className="np-sub me-2" style={{ color: 'var(--bs-danger-text-emphasis)' }}>Delete?</span>
                        <button className="np-btn np-btn-danger np-btn-sm me-1" disabled={deleteReading.isPending} onClick={() => deleteReading.mutate(r.readingId)}>Yes</button>
                        <button className="np-btn np-btn-ghost np-btn-sm" onClick={() => setConfirmDeleteReading(null)}>No</button>
                      </>
                    ) : (
                      <>
                        <button className="np-icon-btn" onClick={() => startEditReading(r)}><i className="ri-edit-line"></i></button>
                        <button className="np-icon-btn np-icon-btn-danger" onClick={() => setConfirmDeleteReading(r.readingId)}><i className="ri-delete-bin-line"></i></button>
                      </>
                    )}
                  </td>
                </tr>
              )
            ))}
          </NimbusTable>
        </div>
      )}
    </div>
  );
};
